import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X as XIcon, Save, Upload, Trash2 } from "lucide-react";
import { api, formatError, resolveAsset } from "../lib/api";

export default function EditTeamModal({ team, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: team.name || "",
    color: team.color || "#E60000",
    seed: team.seed || 0,
    logo_url: team.logo_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/teams/${team.id}`, {
        name: form.name,
        color: form.color,
        seed: Number(form.seed) || 0,
        logo_url: form.logo_url || null,
      });
      toast.success("Team updated");
      onSaved && onSaved(data);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setBusy(false);
  };

  const uploadLogo = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded — click Save to apply");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
    setUploading(false);
  };

  const del = async () => {
    if (!window.confirm(`Remove team "${team.name}"? This will also remove them from the bracket.`)) return;
    try {
      await api.delete(`/teams/${team.id}`);
      toast.success("Team removed");
      onDeleted && onDeleted(team.id);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4">
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-team-modal"
        className="glass-red-glow rounded-2xl max-w-md w-full p-8 relative"
      >
        <button
          onClick={onClose}
          data-testid="edit-team-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
        >
          <XIcon size={14} />
        </button>

        <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono mb-1">Manage team</div>
        <h2 className="font-display font-black text-2xl tracking-tight mb-6">Edit {team.name}</h2>

        <div className="flex items-center gap-4 mb-5">
          {form.logo_url ? (
            <img src={resolveAsset(form.logo_url)} alt="" className="h-16 w-16 rounded-full object-cover border border-white/10" />
          ) : (
            <div className="h-16 w-16 rounded-full flex items-center justify-center font-display font-bold text-xl" style={{ background: form.color }}>
              {(form.name || "?").slice(0, 2).toUpperCase()}
            </div>
          )}
          <label className="btn-ghost-glass !py-2 !px-4 !text-xs cursor-pointer">
            <Upload size={12} /> {uploading ? "Uploading..." : "Upload logo"}
            <input
              type="file" accept="image/*" className="hidden"
              disabled={uploading}
              data-testid="edit-team-logo"
              onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])}
            />
          </label>
        </div>

        <div className="space-y-4 mb-6">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Name</span>
            <input
              data-testid="edit-team-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Color</span>
              <input
                data-testid="edit-team-color" type="color"
                value={form.color}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-2 h-11"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Seed</span>
              <input
                data-testid="edit-team-seed" type="number"
                value={form.seed}
                onChange={(e) => setForm({ ...form, seed: e.target.value })}
                className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
              />
            </label>
          </div>
        </div>

        <div className="flex justify-between gap-2">
          <button onClick={del} data-testid="edit-team-delete" className="btn-ghost-glass !px-4 hover:!text-red-400">
            <Trash2 size={14} /> Remove
          </button>
          <button onClick={save} disabled={busy} data-testid="edit-team-save" className="btn-liquid !px-5">
            <Save size={14} /> {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
